import { useMemo, useState } from 'react';
import {
  AddPriceBandIcon,
  Bin,
  EllipsisMenu,
  LinkButton,
  Prompt,
  SnackbarStatus,
  Table,
  Typography,
  useSnackbar,
  TextIcon,
} from '@weareopenr/component-library';
import { createColumnHelper } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { usePricingCategories } from 'src/hooks/usePricingCategories';
import { INestedPricingCategory } from 'src/api/getPricingCategories/types';
import { createPricingCategory } from 'src/api/createPricingCategory/createPricingCategory';
import { InlineCategoryForm } from 'src/components/VenueCategoriesTab/InlineCategoryForm/InlineCategoryForm';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { QueryKeys } from 'src/hooks/QueryKeys';
import SkeletonContainer from 'src/components/SkeletonContainer';
import styles from './PricingSchemeCategoriesTab.module.scss';
import { useDeletePriceSchemeCategory } from 'src/hooks/useDeletePriceSchemeCategory';
import { updatePricingCategory } from 'src/api/updatePricingCategory';

interface ITableRow extends INestedPricingCategory {
  subcategoriesCount: number | string;
  isUncategorised: boolean;
  isCreateRow: boolean;
  children: ITableRow[];
}

const createRow: ITableRow = {
  isCreateRow: true,
  isUncategorised: false,
  category_id: '',
  name: '',
  parent_category_id: null,
  display_order: 0,
  products_assigned: 0,
  subcategoriesCount: 0,
  children: [],
};

const mapToTableRows = (
  categories: INestedPricingCategory[],
  isCreateOpen: boolean,
  parentCategoryId: string | null,
): ITableRow[] => {
  const rows = categories.map((category) => {
    const subCategoriesCount = category.children.length;
    const children = mapToTableRows(category.children, isCreateOpen, parentCategoryId);
    if (isCreateOpen && parentCategoryId !== null && parentCategoryId === category.category_id) {
      children.push(createRow);
    }
    return {
      ...category,
      isUncategorised: category.category_id === null,
      subcategoriesCount: category.category_id === null ? '-' : subCategoriesCount,
      isCreateRow: false,
      children,
    };
  });

  return rows;
};

const columnHelper = createColumnHelper<ITableRow>();

export const PricingSchemeCategoriesTab = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const { nestedCategories, isLoading, data, queryKey } = usePricingCategories(priceSchemeId);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const deleteCategory = useDeletePriceSchemeCategory(priceSchemeId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [parentCategoryId, setParentCategoryId] = useState<string | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<ITableRow | null>(null);
  const [categoryIdEditingName, setCategoryIdEditingName] = useState<string | null>(null);

  const pricingCategories = data ?? [];
  const areCrudActionsDisabled = isCreateOpen || !!categoryIdEditingName;

  const handleDelete = async () => {
    if (!categoryToDelete?.category_id) return;

    try {
      await deleteCategory.mutateAsync(categoryToDelete.category_id);
      await queryClient.invalidateQueries({ queryKey });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `Category "${categoryToDelete.name}" has been deleted successfully`,
      });
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: `Failed to delete category "${categoryToDelete.name}": ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setCategoryToDelete(null);
    }
  };

  const handleDeleteClick = (category: ITableRow) => setCategoryToDelete(category);

  const handleCreateSubCategoryClick = (categoryId: string, toggleExpanded: (value: boolean) => void) => {
    setParentCategoryId(categoryId);
    setIsCreateOpen(true);
    toggleExpanded(true);
  };

  const handleClose = () => {
    setIsCreateOpen(false);
    setParentCategoryId(null);
    setCategoryIdEditingName(null);
  };

  const handleInlineCategoryFormSubmit = async (name: string, original: ITableRow) => {
    if (original.isCreateRow) {
      await createPricingCategory(priceSchemeId, [{ name, parent_category_id: parentCategoryId }]);
    } else {
      await updatePricingCategory({ priceSchemeId, categoryId: original.category_id, newName: name });
    }

    await queryClient.invalidateQueries({
      queryKey: [getBrandId(), QueryKeys.pricingCategories, priceSchemeId],
    });
  };

  const tableData = useMemo(() => {
    const rows = mapToTableRows(nestedCategories, isCreateOpen, parentCategoryId);
    if (isCreateOpen && parentCategoryId === null) {
      return [...rows, createRow];
    }
    return rows;
  }, [nestedCategories, isCreateOpen, parentCategoryId]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Pricing Categories',
        cell: ({ row: { original }, getValue }) => {
          if (original.isCreateRow || (original.category_id === categoryIdEditingName && !original.isUncategorised)) {
            return (
              <InlineCategoryForm
                onSubmit={(name) => handleInlineCategoryFormSubmit(name, original)}
                onClose={handleClose}
                existingNames={pricingCategories.filter((c) => c.name !== original.name).map((c) => c.name)}
                defaultValue={original.isCreateRow ? '' : original.name}
              />
            );
          }
          if (original.isUncategorised) {
            return <span className={styles.uncategorised}>{getValue()}</span>;
          }
          return getValue();
        },
        footer: () => (
          <LinkButton onClick={() => setIsCreateOpen(true)} disabled={areCrudActionsDisabled}>
            + Create Category
          </LinkButton>
        ),
      }),
      columnHelper.accessor('subcategoriesCount', {
        header: 'Subcategories',
        size: 150,
        cell: ({ row: { original }, getValue }) => {
          if (original.isCreateRow) return null;
          if (original.isUncategorised) {
            return <span className={styles.uncategorised}>{getValue()}</span>;
          }
          return getValue();
        },
      }),
      columnHelper.accessor('products_assigned', {
        header: 'Products Assigned',
        size: 180,
        cell: ({ row: { original }, getValue }) => {
          if (original.isCreateRow) return null;
          if (original.isUncategorised) {
            return <span className={styles.uncategorised}>{getValue()}</span>;
          }
          return getValue();
        },
      }),
      columnHelper.display({
        id: 'action',
        size: 80,
        cell: ({ row: { original, toggleExpanded } }) => {
          if (original.isUncategorised || original.isCreateRow) return null;
          return (
            <EllipsisMenu
              options={[
                {
                  id: 'create-sub-category',
                  name: 'Create Sub-category',
                  icon: AddPriceBandIcon,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => handleCreateSubCategoryClick(original.category_id, toggleExpanded),
                },
                {
                  id: 'edit-name',
                  name: 'Edit Name',
                  icon: TextIcon,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => setCategoryIdEditingName(original.category_id),
                },
                { id: 'divider-2', isDivider: true },
                {
                  id: 'delete-pricing-category',
                  name: 'Delete category',
                  hoverColour: 'tomato',
                  icon: Bin,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => handleDeleteClick(original),
                },
              ]}
              variant="ghost"
              hoverStyle="high-contrast"
            />
          );
        },
      }),
    ],
    [pricingCategories, parentCategoryId, areCrudActionsDisabled, categoryIdEditingName],
  );

  if (isLoading) {
    return <SkeletonContainer hiddenTitle />;
  }

  return (
    <div className={styles['pricing-categories-tab']}>
      <Table
        className={styles['table-fill-height']}
        data={tableData}
        columns={columns}
        getSubRows={(row) =>
          row.isUncategorised || row.isCreateRow || row.children.length === 0 ? undefined : row.children
        }
        maxHeight="82vh"
      />
      {categoryToDelete && (
        <Prompt
          open
          promptHeading={`Are you sure you want to delete "${categoryToDelete.name}"?`}
          closePrompt={() => setCategoryToDelete(null)}
          size="small"
          backButton={true}
          backButtonText="Cancel"
          buttonText="Delete"
          buttonType="button"
          buttonHandler={handleDelete}
          isSubmitting={deleteCategory.isLoading}
        >
          <Typography>
            Deleting this category will result in all associated products to move into “uncategorised” and rules to be
            permanently deleted
          </Typography>
          <br />
          <Typography>This action cannot be undone</Typography>
        </Prompt>
      )}
    </div>
  );
};
